import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

export const delegationStatuses = ['queued', 'working', 'complete', 'failed', 'disabled'] as const;
export const delegationStatusSchema = z.enum(delegationStatuses);
export type DelegationStatus = z.infer<typeof delegationStatusSchema>;

export const delegationSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    parentRunId: idSchema,
    childRunId: idSchema.nullish(),
    fromAgentId: idSchema,
    toAgentId: idSchema,
    status: delegationStatusSchema.default('queued'),
    hops: z.number().int().min(1).default(1),
  })
  .merge(timestampsSchema);
export type Delegation = z.infer<typeof delegationSchema>;

/** A node in the live agent-network view for a run. */
export interface AgentNetworkNode {
  delegationId: string;
  fromAgentId: string;
  fromAgentName?: string;
  toAgentId: string;
  toAgentName?: string;
  childRunId?: string | null;
  status: DelegationStatus;
  childStatus?: string;
  childStage?: string | null;
}

export interface AgentNetwork {
  runId: string;
  agentId: string;
  status: string;
  currentStage?: string | null;
  nodes: AgentNetworkNode[];
}

const ACTIVE_NETWORK_STATUSES = new Set(['queued', 'working', 'running']);

function isActiveNetworkNode(node: AgentNetworkNode): boolean {
  const status = node.childStatus ?? node.status;
  return ACTIVE_NETWORK_STATUSES.has(status);
}

function preferNetworkNode(next: AgentNetworkNode, prev: AgentNetworkNode): boolean {
  const nextActive = isActiveNetworkNode(next);
  const prevActive = isActiveNetworkNode(prev);
  if (nextActive !== prevActive) return nextActive;
  return true;
}

/** One live node per delegated specialist; keeps the active or latest attempt. */
export function dedupeAgentNetworkNodes(nodes: AgentNetworkNode[]): AgentNetworkNode[] {
  const latestByAgent = new Map<string, AgentNetworkNode>();
  const order: string[] = [];

  for (const node of nodes) {
    if (!latestByAgent.has(node.toAgentId)) {
      order.push(node.toAgentId);
    }
    const existing = latestByAgent.get(node.toAgentId);
    if (!existing || preferNetworkNode(node, existing)) {
      latestByAgent.set(node.toAgentId, node);
    }
  }

  return order.map((toAgentId) => latestByAgent.get(toAgentId)!);
}
